import imageMgtService from '../../../../services/imageMgtService';

export default {
  data() {
    return {
      uploadingImage: false,
      visible: false,
      imageList: null,
      fetchingImageList: true,
      pageNo: 1,
      pageSize: 30,

      selectedTab: 'all',
      bookmarkedImageCount: null,

      // Batch upload functionality
      totalImageCount: null,
      selectedFiles: [],
      uploadProgress: 0,
      isUploading: false,
      uploadStatus: 'idle', // idle, uploading, completed, error

      // Failed files tracking
      failedFiles: []
    };
  },
  props: {
    isImagePage: {
      default: false,
      type: Boolean,
      required: false
    }
  },
  methods: {
    onFileSelected() {
      // Prevent automatic upload
      return false;
    },

    onFilesSelected(file, fileList) {
      // Handle single file selection
      if (file && file.raw) {
        this.addFileToSelection(file.raw);
      }
      // Handle multiple files from fileList
      else if (fileList && Array.isArray(fileList)) {
        for (let fileItem of fileList) {
          if (fileItem.raw) {
            this.addFileToSelection(fileItem.raw);
          }
        }
      }
    },

    addFileToSelection(file) {
      // Check if file already exists
      const exists = this.selectedFiles.some((f) => f.file.name === file.name && f.file.size === file.size);
      if (exists) {
        this.addFailedFile(file, 'File is already selected');
        return;
      }

      // Validate file size
      let fileSize = (file.size / 1024 / 1024).toFixed(2);
      if (fileSize > 16) {
        this.addFailedFile(file, `File size (${fileSize}MB) exceeds 16MB limit`);
        return;
      }

      // Check total file count
      if (this.selectedFiles.length >= 10) {
        this.addFailedFile(file, 'Maximum 10 files allowed');
        return;
      }

      // Create preview object
      const filePreview = {
        file: file,
        name: file.name,
        size: fileSize,
        preview: URL.createObjectURL(file),
        status: 'pending' // pending, uploading, success, error
      };

      this.selectedFiles.push(filePreview);
    },

    addFailedFile(file, reason) {
      const fileSize = (file.size / 1024 / 1024).toFixed(2);
      const failedFile = {
        file: file,
        name: file.name,
        size: fileSize,
        reason: reason,
        preview: URL.createObjectURL(file)
      };

      this.failedFiles.push(failedFile);
    },

    removeSelectedFile(index) {
      // Revoke object URL to prevent memory leaks
      URL.revokeObjectURL(this.selectedFiles[index].preview);
      this.selectedFiles.splice(index, 1);
    },

    async uploadAllImages() {
      if (this.selectedFiles.length === 0) {
        this.errorToast('No files selected for upload.');
        return;
      }

      this.isUploading = true;
      this.uploadStatus = 'uploading';
      this.uploadProgress = 0;

      const totalFiles = this.selectedFiles.length;
      let successCount = 0;
      let errorCount = 0;

      // Upload files sequentially
      for (let i = 0; i < this.selectedFiles.length; i++) {
        const filePreview = this.selectedFiles[i];
        filePreview.status = 'uploading';

        try {
          let formData = new FormData();
          formData.append('image', filePreview.file);

          let result = await imageMgtService.uploadImage(formData, this);
          this.imageList.unshift(result.data);
          filePreview.status = 'success';
          successCount++;
        } catch (err) {
          console.log(`Failed to upload file ${filePreview.name}`, err);
          filePreview.status = 'error';
          errorCount++;
        }

        // Update progress
        this.uploadProgress = ((i + 1) / totalFiles) * 100;
      }

      this.isUploading = false;
      this.uploadStatus = errorCount > 0 ? 'error' : 'completed';

      // Show results
      if (successCount > 0) {
        this.successToast(`Successfully uploaded ${successCount} image(s).`);
      }
      if (errorCount > 0) {
        this.errorToast(`Failed to upload ${errorCount} image(s).`);
      }

      // Clear selected files and failed files after upload
      this.clearSelectedFiles();
      this.clearFailedFiles();
    },

    removeFailedFile(index) {
      // Revoke object URL to prevent memory leaks
      URL.revokeObjectURL(this.failedFiles[index].preview);
      this.failedFiles.splice(index, 1);
    },

    clearFailedFiles() {
      // Revoke all object URLs to prevent memory leaks
      this.failedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });
      this.failedFiles = [];
    },

    clearSelectedFiles() {
      // Revoke all object URLs to prevent memory leaks
      this.selectedFiles.forEach((file) => {
        URL.revokeObjectURL(file.preview);
      });
      this.selectedFiles = [];
      this.uploadProgress = 0;
      this.uploadStatus = 'idle';
    },

    clearAllFiles() {
      this.clearSelectedFiles();
      this.clearFailedFiles();
    },

    async deleteImage(image, index) {
      this.imageList.splice(index, 1);
      imageMgtService.deleteCampaignImage(image.id, this).catch((err) => {
        console.log('Failed to delete image', err);
        this.errorToast('Failed delete image, plase contact support.');
      });
    },

    async onTabChange(tab) {
      this.selectedTab = tab;

      // Reset pagination.
      this.pageNo = 1;

      this.fetchImageList(false);
    },

    async fetchImageList(readTotal) {
      this.fetchingImageList = true;

      let params = {
        page: this.pageNo,
        size: this.pageSize,
        total: readTotal ? 1 : 0,
        onlyBookmarked: this.selectedTab === 'bookmarked' ? 1 : 0
      };

      imageMgtService
        .getImages(params, this)
        .then((result) => {
          this.imageList = result.data.list;
          if (result.data.total) {
            this.bookmarkedImageCount = result.data.total.bookmarked;
            this.totalImageCount = result.data.total.all;
          }
          this.fetchingImageList = false;
        })
        .catch((err) => {
          console.log('Failed to fetch images', err);
          this.errorToast('Failed fetch images, plase contact support.');
          this.fetchingImageList = false;
        });
    },

    onPageChange(val) {
      this.pageNo = val;
      this.fetchImageList(false);
    },

    onImageSelected(img) {
      this.visible = false;
      this.$emit('onSelect', img);
    },

    async onCopyImageUrl(img) {
      setTimeout(async () => {
        try {
          await navigator.clipboard.writeText(img.url);
          this.successToast('Link Copied.');
        } catch (err) {
          console.log('Failed to copy image URL', err);
          this.errorToast('Failed to copy image URL, plase contact support.');
        }
      }, 500);
    },

    toggleVisiblity() {
      this.visible = !this.visible;
      if (this.visible && this.imageList == null) {
        this.fetchImageList(true);
      }
    },

    async onToggleImageBookmark(index) {
      try {
        let image = this.imageList[index];
        image.is_bookmarked = !image.is_bookmarked;
        if (image.is_bookmarked) {
          this.bookmarkedImageCount++;
        } else {
          this.bookmarkedImageCount--;
          if (this.selectedTab === 'bookmarked') this.imageList.splice(index, 1);
        }

        await imageMgtService.toggleImageBookmark(
          {
            id: image.id
          },
          this
        );
      } catch (err) {
        console.log('Failed to toggle image bookmark', err);
        this.errorToast('Failed to bookmark image, plase contact support.');
      }
    }
  },

  mounted() {
    if (this.isImagePage) {
      this.fetchImageList(true);
    }
  }
};
